/**
 * AppChrome — the persistent post-auth top bar + nav drawer (MATCHDAY.dc.html
 * "BROADCAST CHROME" + menuDrawer, ported 24 Jul). This is the global chrome
 * BK's real-device review found missing entirely: hamburger → drawer (Locker
 * Room, Trophy Room, Match Centre, Night Room, Help/FAQs/Support, VAR, Terms)
 * and a bell wired to the real actionable-unread count (commsApi precedent —
 * Feed.tsx's own bell chip, now hoisted so every screen carries it, not just
 * the feed).
 *
 * Mount rule: always rendered in App.tsx; renders nothing until a session is
 * confirmed (auth.getSession()) AND the route isn't pre-auth/full-bleed
 * (Landing, /auth, /first-night, /m/:id token redemption) — those screens own
 * their own header treatment or predate having anything to navigate to yet.
 *
 * Honesty notes (no invented functionality, KIT CHECK Rule 1 extended to nav):
 * - No numeric badges on Locker Room / Trophy Room — no cheap count endpoint
 *   exists yet; a fabricated number is worse than none.
 * - "Match Centre" still opens the feed — it has no dedicated list screen yet;
 *   a live match is reached from its card there, and the row says so. ("VAR"
 *   was the same until 25 Jul, when the real Review Room shipped at /var.)
 * - Help / FAQs / Support all land on ONE real screen (/help) rather than
 *   three near-empty stubs — see Help.tsx's own header comment.
 */
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "wouter";
import { ArtImage } from "@matchday/ui";
import { auth } from "../../lib/platform";
import { commsApi } from "../../lib/commsApi";
import { matchApi } from "../../lib/matchApi";
import "../../styles/chrome.css";

/** Routes that own their own header/canvas — the chrome stays off. */
const CHROME_SUPPRESSED_PREFIXES = ["/auth", "/first-night", "/m/", "/share-relay"];

function chromeSuppressedFor(path: string): boolean {
  if (path === "/") return true;
  return CHROME_SUPPRESSED_PREFIXES.some((p) => path.startsWith(p));
}

interface MenuRow {
  label: string;
  to: string;
  caption?: string;
  tag?: { text: string; tone: "live" | "gold" | "kitman" };
  dot?: "red" | "green" | "volt" | "steel";
}

const MENU_ROWS: MenuRow[] = [
  { label: "Locker Room", to: "/locker" },
  { label: "Trophy Room", to: "/cabinet" },
  { label: "The Table", to: "/table" },
  { label: "Match Centre", to: "/feed", caption: "Opens from your active match card", tag: { text: "LIVE", tone: "live" } },
  { label: "The Night Room", to: "/room", dot: "red" },
  { label: "Help", to: "/help", tag: { text: "THE KITMAN", tone: "kitman" } },
  { label: "FAQs", to: "/help" },
  { label: "Support", to: "/help" },
  // UPSTAIRS V1: the badge is state-driven (volt = something needs YOU, steel
  // = a check is running, quiet when clear) and NEVER red — VAR asking for
  // your attention is routine, not alarm. Resolved at render from /var/room.
  { label: "VAR", to: "/var" },
  { label: "Terms and Conditions", to: "/terms" },
];

export function AppChrome(): JSX.Element | null {
  const [location, navigate] = useLocation();
  const [ready, setReady] = useState(false);
  const [open, setOpen] = useState(false);
  const [inboxCount, setInboxCount] = useState(0);
  const [varBadge, setVarBadge] = useState<"volt" | "steel" | null>(null);

  useEffect(() => {
    let alive = true;
    void auth.getSession().then((session) => {
      if (alive) setReady(session !== null);
    });
    return () => {
      alive = false;
    };
  }, [location]);

  // Bell badge — actionable-unread only (TOUCHLINE B2, same source as
  // Feed.tsx's own chip). Best-effort: the chrome never breaks on this.
  useEffect(() => {
    if (!ready) return;
    let alive = true;
    const load = () =>
      void commsApi
        .getInbox()
        .then((v) => {
          if (alive) setInboxCount(v.actionable_unread);
        })
        .catch(() => undefined);
    load();
    const t = setInterval(load, 30_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [ready]);

  // UPSTAIRS V1 — the VAR badge. Only fetched while the drawer is OPEN: it's
  // the only place the badge is visible, so polling it closed would be pure
  // waste. Best-effort, same law as the bell: the chrome never breaks on it.
  useEffect(() => {
    if (!ready || !open) return;
    let alive = true;
    void matchApi
      .getVarRoom()
      .then((v) => {
        if (alive) setVarBadge(v.needs_you_count > 0 ? "volt" : v.checking_count > 0 ? "steel" : null);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [ready, open]);

  // Close the drawer on every navigation.
  useEffect(() => {
    setOpen(false);
  }, [location]);

  const go = useCallback(
    (to: string) => {
      setOpen(false);
      navigate(to);
    },
    [navigate],
  );

  if (!ready || chromeSuppressedFor(location)) return null;

  return (
    <>
      <div className="ac-bar">
        <button type="button" className="ac-hamburger" aria-label="Menu" aria-expanded={open} onClick={() => setOpen(true)}>
          <span />
          <span />
          <span />
        </button>
        <div className="ac-lockup">
          <ArtImage src="/assets/art/blg/blg-logo-w.png" alt="BLG" className="ac-lockup-blg" />
          <span className="ac-lockup-x">×</span>
          <ArtImage src="/assets/art/ea/eafc-logo.webp" alt="EA FC" className="ac-lockup-ea" />
        </div>
        <div className="ac-right">
          <button type="button" className="ac-bell" aria-label="Notifications" onClick={() => go("/inbox")}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.7 21a2 2 0 0 1-3.4 0" />
            </svg>
            {inboxCount > 0 ? <span className="ac-bell-badge">{inboxCount}</span> : null}
          </button>
          <div className="ac-prize">
            <div className="ac-prize-amount">R5,000</div>
            <div className="ac-prize-label">Prize Pool</div>
          </div>
        </div>
      </div>

      <div className={`ac-scrim${open ? " on" : ""}`} onClick={() => setOpen(false)} aria-hidden="true" />
      <aside className={`ac-drawer${open ? " on" : ""}`} aria-hidden={!open}>
        <div className="ac-drawer-head">
          <ArtImage src="/assets/art/blg/blg-logo-w.png" alt="BLG" className="ac-drawer-logo" />
          <button type="button" className="ac-drawer-close" aria-label="Close" onClick={() => setOpen(false)}>
            ✕
          </button>
        </div>
        <div className="ac-drawer-eyebrow">Menu</div>
        <nav className="ac-drawer-nav">
          {MENU_ROWS.map((row) => {
            // VAR's dot is live state (V1), not a static decoration.
            const dot = row.to === "/var" ? varBadge : (row.dot ?? null);
            return (
              <button type="button" key={row.label} className="ac-drawer-row" onClick={() => go(row.to)}>
                <span className="ac-drawer-row-main">
                  <span className="ac-drawer-row-label">{row.label}</span>
                  {row.caption ? <span className="ac-drawer-row-caption">{row.caption}</span> : null}
                </span>
                <span className="ac-drawer-row-side">
                  {row.tag ? <span className={`ac-drawer-tag ac-drawer-tag--${row.tag.tone}`}>{row.tag.text}</span> : null}
                  {dot !== null ? <span className={`ac-drawer-dot ac-drawer-dot--${dot}`} /> : null}
                  <span className="ac-drawer-chevron">›</span>
                </span>
              </button>
            );
          })}
        </nav>
        <div className="ac-drawer-foot">Powered by BLG</div>
      </aside>
    </>
  );
}
